/**
 * ADMIN / INTERNAL OPERATION NAMESPACE (TV1-2051, supersedes scripts `KNOWN_UNGATED_ROUTES` — TV1-1986).
 *
 * The frozen set of operation identities for admin-only / internal private mutation routes.
 * These routes are `private` + gateway-reachable + admin-gated AT THE GATEWAY (`gateway.ts`
 * Step 5b: a private route with no `x-subdomain` is `userType === 'admin'`, 403 otherwise, and
 * runs with `accountId: undefined`). They intentionally own NO org capability.
 *
 * ── Why the identity is `default:<op>` ───────────────────────────────────────
 * Each of these routes calls `.withSubdomain('')` on its aggregate builder, so the openapi
 * carries `x-visibility: private` and NO `x-subdomain`. PV3's client-library generator stamps
 * such private mutations under its `default` fallback module → `operation: 'default:<paramCase(op)>'`
 * in the generated `operations.ts`. That `subdomain: 'default'` metadata is the exact FE-side
 * mirror of the gateway's `!route.subdomain` admin rule — both derive from the same cause.
 *
 * This set gives that identity a first-class MEANING ("admin/internal, resolved without an org
 * capability") and ONE shared definition, consumed by BOTH:
 *   - the FE gate (`gateCommandByOperation`, web-app) — `isAdminOperation(op)` → pass-through
 *     (the gateway Step 5b is the authoritative admin check; the FE `User` carries no admin
 *     signal so it cannot re-check — see TV1-2051 design Q2), and
 *   - the operation-key convergence guard (`scripts/checkOperationKeyConvergence.ts`) — an
 *     admin op is exempt from the `no-capability` check.
 *
 * ── Keyed on the OPERATION IDENTITY, not the route template ───────────────────
 * The join key both consumers share is the client-lib `operation` id (`default:<op>`), NOT the
 * route or its last path segment. Note the op slug comes from `paramCase(operationId)`, which
 * can differ from the URL: e.g. `POST /dev-utils/feature-flags/enable-for-account` →
 * `default:feature-flags-override`.
 *
 * ── FROZEN: the debt can shrink, never silently grow ─────────────────────────
 * A NEW subdomain-less private op that is NOT listed here fails the convergence guard at build
 * time (loud) with a `no-capability` violation, forcing a reviewed decision: is it genuinely
 * admin (add it here) or did it forget an `x-subdomain` (give it one + a capability)?
 *
 * ── Escalation warning (do NOT "fix" by org-scoping) ─────────────────────────
 * Assigning a real `x-subdomain` here would make `route.subdomain` truthy → gateway Step 5b
 * STOPS firing → the route reroutes through org RBAC (a privilege escalation exposing internal
 * money movement to non-admin org users) AND breaks the admin caller (no accountId/feature).
 * These routes must stay `private` + subdomain-less.
 */

export const ADMIN_OPERATIONS: ReadonlySet<string> = new Set([
  // ─── Admin-only: billing money-side aggregate routes (Teselly-operated, not tenant-facing) ───
  // billing-profiles
  'default:register-billing-profile',
  'default:add-payment-method',
  'default:remove-payment-method',
  'default:set-default-payment-method',
  'default:update-billing-profile',
  // billing-accounts
  'default:register-billing-account',
  'default:change-billing-account-currency',
  'default:add-contract',
  'default:add-line-items',
  'default:edit-line-item',
  'default:amend-contract',
  'default:upgrade-product',
  'default:renew-contract',
  'default:expire-contract',
  'default:cancel-billing-contract',
  // billing forced-termination batch cancel (TV1-1752, ADR-127). One command,
  // N `ContractCanceled`, one stream, one transaction on the subdomain-less
  // `BillingAccountsAggregate` — admin/system-only, like every other billing
  // command; it owns NO org capability and nothing is added to `registry.ts`.
  'default:cancel-all-billing-contracts',
  'default:request-billing-account-subscription-invoice',
  'default:unrecord-credited-window',
  // clients-management account suspension (TV1-1783). Admin/system-only,
  // subdomain-less private commands on the `AccountSuspensionAggregate` —
  // relocated here from the retired billing `suspend/resume-billing-account`
  // when non-payment access suspension moved to its owning subdomain.
  //
  // TV1-2071 — TWO INDEPENDENT HOLDS, so four commands. `suspend`/`resume` are the
  // PAYMENT hold (dunning's, dispatched in-process by `NonPaymentSuspensionPolicy`);
  // `place`/`lift-admin-hold` are the ADMIN hold and are what the admin panel's
  // suspend/resume buttons dispatch. All four are the same authorization class: no org
  // capability, gateway-admin-gated at Step 5b.
  'default:suspend-client-account',
  'default:resume-client-account',
  'default:place-admin-hold',
  'default:lift-admin-hold',
  // clients-management account TERMINATION (TV1-1752). Admin/system-only,
  // subdomain-less private command on the `AccountTerminationAggregate` — the same
  // authorization class as suspension and observation: it owns NO org capability and
  // is gateway-admin-gated at Step 5b.
  //
  // 🔴 The matching half of this entry is an ABSENCE: nothing is added to
  // `registry.ts`. That absence is precisely what keeps forced termination off every
  // tenant role. Granting it a real `x-subdomain`/capability would stop Step 5b firing
  // and hand a destructive, irreversible operation to org RBAC.
  'default:cancel-client-account',
  // clients-management account observation mode (TV1-2105). Admin/system-only,
  // subdomain-less private commands on the `AccountObservationAggregate` — same
  // class as suspension: they own NO org capability and are gateway-admin-gated.
  'default:enter-observation',
  'default:go-live',
  'default:set-connector-kind-egress-mode',
  // subscription-invoices
  'default:create-draft-subscription-invoice',
  'default:publish-subscription-invoice',
  'default:cancel-draft-subscription-invoice',
  'default:post-payment-to-subscription-invoice',
  'default:post-credit-to-subscription-invoice',
  'default:unpost-payment-from-subscription-invoice',
  'default:unpost-credit-from-subscription-invoice',
  'default:record-subscription-invoice-standing',
  'default:record-subscription-invoice-email-sent',
  // payments
  'default:create-draft-payment',
  'default:publish-payment',
  'default:cancel-draft-payment',
  'default:collect-payment',
  'default:cancel-payment',
  'default:refund-payment',
  'default:record-refund-outcome',
  'default:reverse-payment',
  'default:retry-payment',
  'default:apply-subscription-invoice-payment',
  'default:apply-debit-payment',
  'default:unapply-subscription-invoice-payment',
  'default:unapply-debit-payment',
  // credits
  'default:create-credit',
  'default:apply-credit-to-debit',
  'default:apply-credit-to-subscription-invoice',
  // debits
  'default:create-debit',
  'default:post-payment-to-debit',
  'default:post-credit-to-debit',
  'default:unpost-payment-from-debit',
  'default:unpost-credit-from-debit',
  // dunning-processes
  'default:start-dunning-process',
  'default:advance-dunning-process',
  'default:resolve-dunning-process',
  'default:abort-dunning-process',
  // gateway-webhook-audit
  'default:record-orphan-webhook',
  // payment-gateway
  'default:charge-gateway',
  'default:refund-gateway',

  // ─── Non-prod internal: dev-utils (also non-prod-gated at the composition root; `registry.ts` ───
  // ─── forbids wrapping these under any Capability). Op slug ≠ last path segment — see header.  ───
  'default:feature-flags-override', // POST /dev-utils/feature-flags/enable-for-account
  'default:spreadsheet-bench-save-grid', // POST /dev-utils/spreadsheet-bench/:gridId
  'default:spreadsheet-bench-seed-grid', // POST /dev-utils/spreadsheet-bench/:gridId/seed

  // ─── pv3 ADMIN PANEL — outbox-plugin mutations (`/admin/api/outbox/*`) ───────
  // `setupAdminServices.ts` registers the panel with an EMPTY subdomain, which is
  // this file's whole subject: private + subdomain-less + gateway-admin-gated at
  // Step 5b, owning no org capability. It previously passed `subdomain: 'generic'`,
  // which routed the ENTIRE panel — its own session check included — through org
  // RBAC and answered `401 Account context required`, because a platform admin is
  // a member of no organization. That is precisely the escalation this file's
  // header warns against, and the reason these belong here rather than in a
  // Capability. The four listed are the panel's client-library MUTATIONS; its
  // reads (`session`, `config`, `manifest.json`, `cluster-structure`, `get-*`)
  // carry no client-lib operation and are gated the same way at the gateway.
  // Op slug ≠ last path segment on `restart-data-split` — see the header.
  'default:drain-dlq', // POST /admin/api/outbox/drain-dlq/:subscriptionId
  'default:restart-data-split', // POST /admin/api/outbox/restart-datasplit
  'default:retry-stream', // POST /admin/api/outbox/retry-stream
  'default:scoped-refill' // POST /admin/api/outbox/scoped-refill/:subscriptionId
]);

/**
 * True when `operation` is an admin-only / internal op resolved WITHOUT an org capability.
 * `undefined`-safe: `public`/`internal` client-lib entries omit `operation`, and a command
 * absent from any `CommandOperationMap` resolves to `undefined` — neither is an admin op.
 */
export const isAdminOperation = ( operation: string | undefined ): boolean =>
  operation !== undefined && ADMIN_OPERATIONS.has( operation );
